/**
 * Roadmap Operations
 * 
 * Provides CRUD operations for roadmap macro, meso, and weekly reports
 * with proper offline handling, conflict detection, and sync queue management.
 */

import { BaseOperations } from './shared';
import { STORES } from '../schema';
import type { 
  RoadmapMacroOffline, 
  RoadmapMesoOffline, 
  WeeklyReportOffline 
} from '../schema';
import { offlineDB } from '../database';

/**
 * Roadmap Macro Operations
 */
export class RoadmapMacroOperations extends BaseOperations<RoadmapMacroOffline> {
  constructor() {
    super(STORES.ROADMAP_MACRO);
  }

  /**
   * Get active macro curriculum items
   */
  async getActive(): Promise<RoadmapMacroOffline[]> {
    return this.queryByIndex('by_is_active', 1); // Use 1 for true in IndexedDB
  }

  /**
   * Get macro curriculum by belt and stripe
   */
  async getByBeltAndStripe(beltColor: string, stripeCount: number): Promise<RoadmapMacroOffline[]> {
    return this.queryByIndex('by_belt_stripe', [beltColor, stripeCount]);
  }

  /**
   * Get macro curriculum by belt color
   */
  async getByBeltColor(beltColor: string): Promise<RoadmapMacroOffline[]> {
    return this.queryByIndex('by_belt_color', beltColor);
  }

  /**
   * Get all macro curriculum ordered by order_index
   */
  async getAllOrdered(): Promise<RoadmapMacroOffline[]> {
    const all = await this.getAll();
    return all.sort((a, b) => a.order_index - b.order_index);
  }
}

/**
 * Roadmap Meso Operations
 */
export class RoadmapMesoOperations extends BaseOperations<RoadmapMesoOffline> {
  constructor() {
    super(STORES.ROADMAP_MESO);
  }

  /**
   * Get active meso curriculum items
   */
  async getActive(): Promise<RoadmapMesoOffline[]> {
    return this.queryByIndex('by_is_active', 1); // Use 1 for true in IndexedDB
  }

  /**
   * Get meso curriculum by belt and stripe
   */
  async getByBeltAndStripe(beltColor: string, stripeCount: number): Promise<RoadmapMesoOffline[]> {
    return this.queryByIndex('by_belt_stripe', [beltColor, stripeCount]);
  }

  /**
   * Get meso curriculum by belt color
   */
  async getByBeltColor(beltColor: string): Promise<RoadmapMesoOffline[]> {
    return this.queryByIndex('by_belt_color', beltColor);
  }

  /**
   * Get all meso curriculum ordered by order_index
   */
  async getAllOrdered(): Promise<RoadmapMesoOffline[]> {
    const all = await this.getAll();
    return all.sort((a, b) => a.order_index - b.order_index);
  }

  /**
   * Search meso curriculum by technique
   */
  async searchByTechnique(techniqueName: string): Promise<RoadmapMesoOffline[]> {
    const all = await this.getAll();
    const lowerTechnique = techniqueName.toLowerCase();

    return all.filter(meso =>
      meso.key_techniques?.some(tech => tech.toLowerCase().includes(lowerTechnique))
    );
  }
}

/**
 * Weekly Reports Operations
 */
export class WeeklyReportsOperations extends BaseOperations<WeeklyReportOffline> {
  constructor() {
    super(STORES.WEEKLY_REPORTS);
  }

  /**
   * Get weekly reports by user ID
   */
  async getByUserId(userId: string): Promise<WeeklyReportOffline[]> {
    return this.queryByIndex('by_user_id', userId);
  }

  /**
   * Get weekly report by user and week
   */
  async getByUserAndWeek(userId: string, weekStart: string): Promise<WeeklyReportOffline | null> {
    const reports = await this.queryByIndex('by_user_week', [userId, weekStart]);
    return reports.length > 0 ? reports[0] : null;
  }

  /**
   * Get recent weekly reports (last 90 days)
   */
  async getRecentReports(userId: string, days = 90): Promise<WeeklyReportOffline[]> {
    const cutoffDate = new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
    const userReports = await this.getByUserId(userId);

    return userReports
      .filter(report => report.week_start >= cutoffDate)
      .sort((a, b) => new Date(b.week_start).getTime() - new Date(a.week_start).getTime()); // Newest first
  }

  /**
   * Get reports with training data
   */
  async getReportsWithTraining(userId: string): Promise<WeeklyReportOffline[]> {
    const userReports = await this.getByUserId(userId);
    return userReports.filter(report => report.has_training === true);
  }

  /**
   * Get reports by insight type
   */
  async getByInsightType(insightType: string): Promise<WeeklyReportOffline[]> {
    return this.queryByIndex('by_insight_type', insightType);
  }

  /**
   * Cache weekly report from API response
   */
  async cacheFromAPI(apiReport: Record<string, unknown>): Promise<WeeklyReportOffline> {
    const now = new Date().toISOString();

    const offlineReport: WeeklyReportOffline = {
      id: apiReport.id as string,
      user_id: apiReport.user_id as string,
      week_start: apiReport.week_start as string,
      ai_insights: apiReport.ai_insights as string || undefined,
      summary: apiReport.summary as string || undefined,
      email_status: apiReport.email_status as WeeklyReportOffline['email_status'] || {
        status: 'pending',
        attempts: 0
      },
      feedback_rating: apiReport.feedback_rating as string || undefined,
      has_training: apiReport.has_training as boolean || undefined,
      included_competition_data: apiReport.included_competition_data as boolean || undefined,
      insight_type: apiReport.insight_type as string || undefined,
      model_version: apiReport.model_version as string || undefined,
      recommendations: apiReport.recommendations as Record<string, unknown> || undefined,
      technique_tags: apiReport.technique_tags as string[] || undefined,
      training_count: apiReport.training_count as number || undefined,
      training_minutes: apiReport.training_minutes as number || undefined,
      competition_summary: apiReport.competition_summary as Record<string, unknown> || undefined,
      viewed_at: apiReport.viewed_at as string || undefined,
      created_at: apiReport.created_at as string || now,
      updated_at: apiReport.updated_at as string || now,
      _sync_status: 'synced' as const
    };

    await offlineDB.put(this.storeName, offlineReport);
    return offlineReport;
  }

  /**
   * Bulk cache multiple reports
   */
  async bulkCacheFromAPI(apiReports: Record<string, unknown>[]): Promise<WeeklyReportOffline[]> {
    const cachedReports: WeeklyReportOffline[] = [];

    for (const apiReport of apiReports) {
      const cached = await this.cacheFromAPI(apiReport);
      cachedReports.push(cached);
    }

    return cachedReports;
  }
}

// Export singleton instances
export const roadmapMacroOps = new RoadmapMacroOperations();
export const roadmapMesoOps = new RoadmapMesoOperations();
export const weeklyReportsOps = new WeeklyReportsOperations();
